import React, { useState, useEffect } from 'react';
import './Auth.css';
import DemoLogin from './DemoLogin';
import { GOOGLE_CLIENT_ID } from '../config/google';

const API_URL = 'https://mega-brasil-api.cejusc.workers.dev';

function Login({ onLogin, onClose }) {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);

  useEffect(() => {
    // Verificar se o Google Client ID foi configurado
    const isConfigured = GOOGLE_CLIENT_ID && 
                         GOOGLE_CLIENT_ID !== "your-client-id-here.apps.googleusercontent.com" &&
                         GOOGLE_CLIENT_ID.includes('.apps.googleusercontent.com');
    
    console.log('Google OAuth Check:', {
      clientId: GOOGLE_CLIENT_ID,
      isConfigured,
      hasGoogleInName: GOOGLE_CLIENT_ID?.includes('.apps.googleusercontent.com')
    });
    
    if (isConfigured) {
      // Carregar Google Sign-In apenas se estiver configurado
      loadGoogleSignIn();
    }
  }, [isLogin]);

  const loadGoogleSignIn = () => {
    // Carregar Google Sign-In Script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      if (window.google && GOOGLE_CLIENT_ID) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleLogin,
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        // Renderizar o botão
        const buttonContainer = document.getElementById('googleSignInButton');
        if (buttonContainer) {
          window.google.accounts.id.renderButton(buttonContainer, {
            theme: 'outline',
            size: 'large',
            width: '100%',
            text: isLogin ? 'signin_with' : 'signup_with',
            shape: 'rectangular',
            logo_alignment: 'left'
          });
          
          setGoogleReady(true);
        }
      }
    };

    script.onerror = () => {
      console.error('Erro ao carregar Google Sign-In');
    };

    document.body.appendChild(script);

    return () => {
      // Cleanup: remover script quando componente desmontar
      const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (existingScript) {
        document.body.removeChild(existingScript);
      }
    };
  };

  const handleGoogleLogin = async (response) => {
    setLoading(true);
    setError('');
    
    try {
      const res = await fetch(`${API_URL}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: response.credential })
      });

      const data = await res.json();
      
      if (res.ok) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        onLogin(data.user);
      } else {
        setError(data.error || 'Erro ao fazer login com Google');
      }
    } catch (err) {
      setError('Erro de conexão. Tente novamente.');
    }
    
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
    const body = isLogin 
      ? { email: formData.email, password: formData.password }
      : formData;

    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (response.ok) {
        if (!isLogin) {
          setMessage('Cadastro realizado! Verifique seu email para ativar sua conta.');
        }
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setTimeout(() => onLogin(data.user), 2000);
      } else {
        setError(data.error || 'Erro ao processar solicitação');
      }
    } catch (err) {
      setError('Erro de conexão. Tente novamente.');
    }

    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Se o email estiver cadastrado, você receberá instruções de recuperação.');
        setShowForgotPassword(false);
      } else {
        setError(data.error || 'Erro ao processar solicitação');
      }
    } catch (err) {
      setError('Erro de conexão. Tente novamente.');
    }

    setLoading(false);
  };

  return (
    <div className="auth-modal">
      <div className="auth-container">
        <button className="close-btn" onClick={onClose}>✕</button>
        
        <h2>{showForgotPassword ? 'Recuperar Senha' : (isLogin ? 'Entrar' : 'Criar Conta')}</h2>
        
        {error && <div className="error-message">{error}</div>}
        {message && <div className="success-message">{message}</div>}

        {showForgotPassword ? (
          <form onSubmit={handleForgotPassword}>
            <div className="form-group">
              <input
                type="email"
                placeholder="Seu email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
              />
            </div>
            
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? 'Enviando...' : 'Enviar Instruções'}
            </button>
            
            <button 
              type="button" 
              className="btn-link"
              onClick={() => setShowForgotPassword(false)}
            >
              Voltar ao login
            </button>
          </form>
        ) : (
          <>
            <form onSubmit={handleSubmit}>
              {!isLogin && (
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Seu nome"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required={!isLogin}
                  />
                </div>
              )}
              
              <div className="form-group">
                <input
                  type="email"
                  placeholder="Seu email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-group">
                <input
                  type="password"
                  placeholder="Sua senha"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  minLength={8}
                />
              </div>
              
              {isLogin && (
                <button 
                  type="button" 
                  className="btn-link forgot-link"
                  onClick={() => setShowForgotPassword(true)}
                >
                  Esqueci minha senha
                </button>
              )}
              
              <button type="submit" className="btn-submit" disabled={loading}>
                {loading ? 'Processando...' : (isLogin ? 'Entrar' : 'Criar Conta')}
              </button>
            </form>

            <div className="divider">
              <span>ou</span>
            </div>

            <div className="google-oauth-section">
              {googleReady ? (
                <div id="googleSignInButton"></div>
              ) : (
                <div className="google-oauth-placeholder">
                  <button className="btn-google-disabled" disabled>
                    <img src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHZpZXdCb3g9IjAgMCAxOCAxOCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPgogICAgPHBhdGggZD0iTTkgMThjNC45NyAwIDktNC4wMyA5LTlzLTQuMDMtOS05LTktOSA0LjAzIDkgOSA0LjAzIDkgOSA5WiIgZmlsbD0iIzQyODVGNCIvPgogICAgPHBhdGggZD0iTTkgN2MxLjEgMCAyIC45IDIgMnMtLjkgMi0yIDItMi0uOS0yLTIgLjktMiAyLTJaIiBmaWxsPSIjZmZmIi8+CiAgPC9nPgo8L3N2Zz4K" alt="Google" />
                    Entrar com Google
                  </button>
                  <p className="oauth-note">
                    🔧 Para habilitar Google OAuth, siga as instruções em GOOGLE_OAUTH_SETUP.md
                  </p>
                </div>
              )}
            </div>

            <p className="switch-mode">
              {isLogin ? 'Não tem conta?' : 'Já tem conta?'}
              <button 
                className="btn-link"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError('');
                  setMessage('');
                }}
              >
                {isLogin ? 'Criar conta' : 'Entrar'}
              </button>
            </p>

            <div className="demo-section">
              <div className="divider">
                <span>Testar sem cadastro</span>
              </div>
              <DemoLogin onDemoLogin={onLogin} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Login;